<template>
  <div class="correction-changes">
    <section v-if="billingRows.length">
      <div class="subtitle-2 mb-1">
        Reading and bill changes
      </div>
      <div class="caption text--secondary mb-2">
        The existing and revised monthly reading ranges are shown side by side.
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Existing reading</th>
            <th>Revised reading</th>
            <th>Units<br><small>Existing &rarr; revised</small></th>
            <th>Bill (KSh)<br><small>Existing &rarr; revised</small></th>
            <th>Outstanding (KSh)<br><small>Existing &rarr; revised</small></th>
            <th>Status<br><small>Existing &rarr; revised</small></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in billingRows"
            :key="row.id"
          >
            <td class="month-cell">
              {{ row.month }}
              <small class="d-block text--secondary">{{ billingLabel(row) }}</small>
            </td>
            <td class="reading-cell">
              {{ readingRange(row.before) }}
            </td>
            <td class="reading-cell">
              {{ readingRange(row.after) }}
              <small
                v-if="row.after.reading_baseline"
                class="d-block text--secondary"
              >Opening / reset baseline</small>
            </td>
            <td>
              {{ units(row.before) }} &rarr; {{ units(row.after) }}
              <small
                v-if="row.after.adjusted_units !== null"
                class="d-block text--secondary"
              >Estimated: {{ row.after.estimation_basis }}</small>
            </td>
            <td>
              {{ money(row.before.bill) }} &rarr; {{ money(row.after.bill) }}
              <small class="d-block text--secondary">
                Standing charge: {{ money(row.before.service_fee) }} &rarr; {{ money(row.after.service_fee) }}
              </small>
            </td>
            <td>{{ money(row.before.outstanding) }} &rarr; {{ money(row.after.outstanding) }}</td>
            <td>{{ status(row.before.status) }} &rarr; {{ status(row.after.status) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </section>

    <section
      v-if="paymentRows.length"
      :class="{ 'mt-6': billingRows.length }"
    >
      <v-alert
        type="info"
        text
        dense
        class="mb-2"
      >
        <strong>Payment allocation updates</strong><br>
        These months have no reading or bill changes. They are listed separately because payment
        coverage is recalculated after the correction changes the account total.
      </v-alert>
      <v-simple-table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Outstanding (KSh)<br><small>Existing &rarr; revised</small></th>
            <th>Status<br><small>Existing &rarr; revised</small></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in paymentRows"
            :key="row.id"
          >
            <td>{{ row.month }}</td>
            <td>{{ money(row.before.outstanding) }} &rarr; {{ money(row.after.outstanding) }}</td>
            <td>{{ status(row.before.status) }} &rarr; {{ status(row.after.status) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </section>

    <v-alert
      v-if="unchangedRows.length"
      type="success"
      text
      dense
      :class="{ 'mt-6': billingRows.length || paymentRows.length }"
    >
      <strong>No changes for selected {{ unchangedRows.length === 1 ? 'month' : 'months' }}:</strong>
      {{ unchangedRows.map(row => row.month).join(', ') }}.
    </v-alert>
  </div>
</template>

<script>
export default {
  props: { rows: { type: Array, default: () => [] } },
  data: () => ({ statuses: ['Unpaid', 'Paid', 'Partially paid', 'Overpaid'] }),
  computed: {
    billingRows() {
      return this.rows.filter(row => this.hasReadingOrBillChange(row))
    },
    paymentRows() {
      return this.rows.filter(row => !this.hasReadingOrBillChange(row) && this.hasPaymentChange(row))
    },
    unchangedRows() {
      return this.rows.filter(row => !this.hasReadingOrBillChange(row) && !this.hasPaymentChange(row))
    },
  },
  methods: {
    readingRange(row) {
      return `${row.previous_reading} → ${row.current_reading}`
    },
    units(row) {
      return row.adjusted_units === null
        ? row.current_reading - row.previous_reading
        : row.adjusted_units
    },
    money(value) {
      return Number(value).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    status(value) {
      return this.statuses[value] || 'Unknown'
    },
    hasReadingOrBillChange(row) {
      return row.before.previous_reading !== row.after.previous_reading
        || row.before.current_reading !== row.after.current_reading
        || row.before.adjusted_units !== row.after.adjusted_units
        || row.before.reading_baseline !== row.after.reading_baseline
        || row.before.bill !== row.after.bill
        || row.before.service_fee !== row.after.service_fee
    },
    hasPaymentChange(row) {
      return row.before.outstanding !== row.after.outstanding
        || row.before.status !== row.after.status
    },
    billingLabel(row) {
      return row.selected ? 'Selected correction' : 'Linked from an earlier reading'
    },
  },
}
</script>

<style scoped>
.correction-changes th small {
  font-weight: normal;
  white-space: nowrap;
}

.month-cell,
.reading-cell {
  white-space: nowrap;
}
</style>
